import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import {
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  ExternalLink,
  MessageSquare,
  MoreHorizontal,
  Loader2,
  RefreshCw,
  MapPin,
  Calendar,
  User,
  AlertCircle,
} from "lucide-react";
import Loading from "../components/Loading";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://api.littleexplorers.help";

const STATUS_OPTIONS = [
  { value: "all", label: "All Requests", color: "gray" },
  { value: "pending", label: "Pending", color: "amber" },
  { value: "reviewed", label: "Reviewed", color: "blue" },
  { value: "added", label: "Added", color: "green" },
  { value: "rejected", label: "Rejected", color: "red" },
  { value: "cancelled", label: "Cancelled", color: "gray" },
];

const getStatusColor = (status) => {
  const colors = {
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    reviewed: "bg-blue-100 text-blue-800 border-blue-200",
    added: "bg-green-100 text-green-800 border-green-200",
    rejected: "bg-red-100 text-red-800 border-red-200",
    cancelled: "bg-gray-100 text-gray-800 border-gray-200",
  };
  return colors[status] || colors.pending;
};

const getStatusIcon = (status) => {
  switch (status) {
    case "pending":
      return <Clock className="w-4 h-4" />;
    case "reviewed":
      return <Eye className="w-4 h-4" />;
    case "added":
      return <CheckCircle className="w-4 h-4" />;
    case "rejected":
      return <XCircle className="w-4 h-4" />;
    case "cancelled":
      return <XCircle className="w-4 h-4" />;
    default:
      return <Clock className="w-4 h-4" />;
  }
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ActivityRequests = () => {
  const { getAccessToken } = useAuth();
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");

  useEffect(() => {
    fetchRequests();
  }, [selectedStatus]);

  const fetchRequests = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const token = getAccessToken();
      if (!token) return;

      const queryParams = new URLSearchParams();
      if (selectedStatus !== "all") {
        queryParams.append("status", selectedStatus);
      }

      const response = await fetch(
        `${API_BASE_URL}/admin/activity-requests?${queryParams}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch requests");

      const data = await response.json();
      setRequests(data.requests || []);
    } catch (err) {
      console.error("Error fetching requests:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const updateRequestStatus = async (id, status) => {
    try {
      setIsUpdating(true);
      const token = getAccessToken();
      if (!token) return;

      const body = { status };
      if (status === "rejected" && rejectionReason) {
        body.rejectionReason = rejectionReason;
      }
      if (adminNotes) {
        body.adminNotes = adminNotes;
      }

      const response = await fetch(
        `${API_BASE_URL}/admin/activity-requests/${id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) throw new Error("Failed to update request");

      // Refresh the list
      await fetchRequests();
      setShowDetailModal(false);
      setAdminNotes("");
      setRejectionReason("");
    } catch (err) {
      console.error("Error updating request:", err);
      setError(err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const filteredRequests = requests.filter((request) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      request.activityName?.toLowerCase().includes(query) ||
      request.providerName?.toLowerCase().includes(query) ||
      request.city?.toLowerCase().includes(query) ||
      request.userEmail?.toLowerCase().includes(query)
    );
  });

  const openDetailModal = (request) => {
    setSelectedRequest(request);
    setAdminNotes(request.adminNotes || "");
    setRejectionReason(request.rejectionReason || "");
    setShowDetailModal(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-1">
            Activity Requests
          </h1>
          <p className="text-[var(--text-tertiary)]">
            Manage user-submitted activity requests
          </p>
        </div>
        <button
          onClick={fetchRequests}
          className="btn-secondary flex items-center gap-2"
          disabled={isLoading}
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {STATUS_OPTIONS.map((option) => {
          const count =
            option.value === "all"
              ? requests.length
              : requests.filter((r) => r.status === option.value).length;
          return (
            <button
              key={option.value}
              onClick={() => setSelectedStatus(option.value)}
              className={`card p-4 text-left transition-all ${
                selectedStatus === option.value
                  ? "ring-2 ring-[var(--primary-500)]"
                  : ""
              }`}
            >
              <p className="text-2xl font-bold text-[var(--text-primary)]">
                {count}
              </p>
              <p className="text-sm text-[var(--text-tertiary)]">
                {option.label}
              </p>
            </button>
          );
        })}
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder="Search by activity, provider, or location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="card p-4 border-red-200 bg-red-50 dark:bg-red-900/20">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <p>{error}</p>
          </div>
        </div>
      )}

      {/* Requests Table */}
      <div className="card overflow-hidden">
        {filteredRequests.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-[var(--bg-tertiary)] flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-[var(--text-muted)]" />
            </div>
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-1">
              No requests found
            </h3>
            <p className="text-[var(--text-tertiary)]">
              {searchQuery
                ? "Try adjusting your search terms"
                : "No activity requests match the selected filter"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[var(--bg-secondary)] border-b border-[var(--border-primary)]">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Activity
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Provider
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Location
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-primary)]">
                {filteredRequests.map((request) => (
                  <tr
                    key={request.id}
                    className="hover:bg-[var(--bg-secondary)] transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-[var(--text-primary)]">
                          {request.activityName}
                        </p>
                        {request.childAge && (
                          <p className="text-sm text-[var(--text-muted)]">
                            For age {request.childAge}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-[var(--text-primary)]">
                          {request.providerName}
                        </p>
                        {request.providerWebsite && (
                          <a
                            href={request.providerWebsite}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-[var(--primary-600)] hover:text-[var(--primary-700)] flex items-center gap-1"
                          >
                            Website
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-[var(--text-secondary)]">
                        <MapPin className="w-4 h-4" />
                        <span>
                          {request.city}, {request.state}
                        </span>
                      </div>
                      {request.address && (
                        <p className="text-sm text-[var(--text-muted)]">
                          {request.address}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(
                          request.status
                        )}`}
                      >
                        {getStatusIcon(request.status)}
                        {request.status.charAt(0).toUpperCase() +
                          request.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-[var(--text-secondary)]">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(request.submittedAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => openDetailModal(request)}
                        className="btn-secondary py-1.5 px-3 text-sm"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-[var(--bg-primary)] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[var(--border-primary)]">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-[var(--text-primary)]">
                  Review Activity Request
                </h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Activity Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                    Activity Name
                  </label>
                  <p className="text-[var(--text-primary)] font-medium">
                    {selectedRequest.activityName}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                      Provider
                    </label>
                    <p className="text-[var(--text-primary)]">
                      {selectedRequest.providerName}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                      Website
                    </label>
                    {selectedRequest.providerWebsite ? (
                      <a
                        href={selectedRequest.providerWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[var(--primary-600)] hover:text-[var(--primary-700)] flex items-center gap-1"
                      >
                        Visit Website
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    ) : (
                      <p className="text-[var(--text-muted)]">Not provided</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                    Location
                  </label>
                  <p className="text-[var(--text-primary)]">
                    {selectedRequest.address
                      ? `${selectedRequest.address}, `
                      : ""}
                    {selectedRequest.city}, {selectedRequest.state}
                  </p>
                </div>

                {selectedRequest.childAge && (
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                      Target Age
                    </label>
                    <p className="text-[var(--text-primary)]">
                      {selectedRequest.childAge} years old
                    </p>
                  </div>
                )}

                {selectedRequest.additionalInfo && (
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">
                      Additional Information
                    </label>
                    <p className="text-[var(--text-secondary)] bg-[var(--bg-secondary)] p-3 rounded-lg">
                      {selectedRequest.additionalInfo}
                    </p>
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
                  <User className="w-4 h-4" />
                  <span>Submitted by {selectedRequest.userEmail}</span>
                  <span>•</span>
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(selectedRequest.submittedAt)}</span>
                </div>
              </div>

              {/* Admin Notes */}
              <div className="border-t border-[var(--border-primary)] pt-4">
                <label className="block text-sm font-medium text-[var(--text-muted)] mb-2">
                  Admin Notes
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add internal notes about this request..."
                  className="input w-full h-24 resize-none"
                />
              </div>

              {/* Rejection Reason (only for rejected status) */}
              {selectedRequest.status === "rejected" && (
                <div className="border-t border-[var(--border-primary)] pt-4">
                  <label className="block text-sm font-medium text-[var(--text-muted)] mb-2">
                    Rejection Reason
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Explain why this request was rejected..."
                    className="input w-full h-24 resize-none"
                  />
                </div>
              )}

              {/* Actions */}
              <div className="border-t border-[var(--border-primary)] pt-4">
                <div className="flex flex-wrap gap-2">
                  {selectedRequest.status !== "added" && (
                    <button
                      onClick={() => updateRequestStatus(selectedRequest.id, "added")}
                      disabled={isUpdating}
                      className="btn-success flex items-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Mark as Added
                    </button>
                  )}

                  {selectedRequest.status !== "reviewed" && (
                    <button
                      onClick={() => updateRequestStatus(selectedRequest.id, "reviewed")}
                      disabled={isUpdating}
                      className="btn-primary flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      Mark as Reviewed
                    </button>
                  )}

                  {selectedRequest.status !== "rejected" && (
                    <button
                      onClick={() => updateRequestStatus(selectedRequest.id, "rejected")}
                      disabled={isUpdating}
                      className="btn-danger flex items-center gap-2"
                    >
                      <XCircle className="w-4 h-4" />
                      Reject
                    </button>
                  )}

                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="btn-secondary ml-auto"
                    disabled={isUpdating}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityRequests;
